import { Box, Button, Circle, Flex } from "@chakra-ui/react"

import { CheckIcon } from "../Icons"
import type { ProgressState } from "./types"

type StepProps = React.PropsWithChildren<{
  state: ProgressState
  label?: string
  onClick?: () => void
  number: number
  stepWidth?: number
  // Small screens: only the circles and the current step's label, so the steps fit the width.
  compact?: boolean
}>

// Design system stepper (stepper.css): done is black, the current step is the single red accent,
// what is ahead stays hairline (ADR 0001). The numbered layout is kept.
const getStateColors = (state: ProgressState) => {
  switch (state) {
    case "active":
      return {
        circleBg: "accent",
        circleBorder: "accent",
        circleContrastText: "onAccent",
        labelColor: "text",
      }
    case "complete":
      return {
        circleBg: "surfaceInverse",
        circleBorder: "text",
        circleContrastText: "textInverse",
        labelColor: "text",
      }
    case "incomplete":
      return {
        circleBg: "surface",
        circleBorder: "lineStrong",
        circleContrastText: "textSubtle",
        labelColor: "textSubtle",
      }
  }
}

export const Step = ({ label, state, onClick, number, stepWidth, compact }: StepProps) => {
  const { circleBg, circleBorder, circleContrastText, labelColor } = getStateColors(state)

  return (
    <Box as="li" display="inline-flex" bg="surface" zIndex={1} px={compact ? "2" : "4"} minW="0">
      <Flex
        as={Button}
        onClick={onClick}
        alignItems="center"
        gap={2}
        disabled={state === "incomplete"}
        variant="transparent"
        p="0"
        minH="auto"
        textTransform="none"
        letterSpacing="normal"
        _hover={{ bg: "transparent" }}
      >
        <Circle
          aria-hidden
          zIndex={1}
          position="relative"
          size="6"
          bg={circleBg}
          border="2px solid"
          borderColor={circleBorder}
        >
          {state === "complete" ? (
            <CheckIcon boxSize="4" color={circleContrastText} />
          ) : (
            // Lining figures: Raleway's default old-style figures sit off-centre in the circle.
            <Box
              as="span"
              color={circleContrastText}
              fontSize="3xs"
              fontWeight="semibold"
              lineHeight="1"
              sx={{ fontVariantNumeric: "lining-nums tabular-nums" }}
            >
              {number}
            </Box>
          )}
        </Circle>
        <Box srOnly>{state === "complete" ? `${label ?? ""} - Completed` : label}</Box>
        <Box
          as="span"
          display={compact && state !== "active" ? "none" : undefined}
          // As wide as the label, up to stepWidth, and wrapped between words only: in a fixed 96px
          // the uppercase, tracked words overflowed and Chakra's break-word split them mid-word.
          w={compact ? "auto" : "max-content"}
          maxW={compact ? undefined : (stepWidth ?? 40)}
          textAlign="left"
          whiteSpace="normal"
          overflowWrap="normal"
          fontSize="3xs"
          fontWeight="semibold"
          letterSpacing="wider"
          textTransform="uppercase"
          lineHeight="snug"
          color={labelColor}
        >
          {label}
        </Box>
      </Flex>
    </Box>
  )
}
